import { type ChangeEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppSelector } from "hooks/storeHooks"
import { useDebounce } from "hooks/useDebounce"
import { useAlert } from "providers/AlertProvider"
import { tintVTOService } from "services/tintvto"
import { capturePlatformEvent } from "services/platformAnalytics"
import { selectUserInfo } from "store/user/userSlice"
import { IntegrationType, type GlassesDraftListItem, type Product, type ProductV2 } from "types"
import { parseQuery } from "utils/parseQuery"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { adminApi, type IntegrationQrCodeResponse } from "@/services/adminApi"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FileSpreadsheet, PenLine, Plus, Search, Share2, Upload, X } from "lucide-react"
import ConfirmDialog from "components/ConfirmDialog"
import EmptyList from "components/EmptyList"
import Paginator from "components/Paginator"
import { AddProductsModal } from "../AddProductsModal"
import { AddProductCard } from "../AddProductsModal/AddProductCard"
import { UploadBulkProducts } from "../UploadBulkProducts/UploadBulkProducts"
import { ProductCategories } from "components/Navbar/types"
import {
  ALL_GLASSES_DRAFTS_QUERY_KEY,
  PRODUCTS_QUERY_KEY,
  useGlassesDraftsQuery,
  type ProductsQueryParams,
  useDeleteProductMutation,
  useProductsQuery,
  useToggleProductVisibilityMutation,
} from "hooks/query/useProductsQuery"
import { LeaveOpinionFeatureModal } from "@/features/leaveOpition/components/LeaveOpinionFeatureModal"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"
import { ActivatedOpinionPopup } from "@/features/leaveOpition/LeaveOpinionFeatureService"
import { GlassesRow } from "./GlassesRow"
import { GlassesSelectionToolbar } from "./GlassesSelectionToolbar"
import { GlassesSetupSteps } from "./GlassesSetupSteps"
import { MakeupRow } from "./MakeupRow"
import { MakeupSelectionToolbar } from "./MakeupSelectionToolbar"
import { MakeupSetupSteps } from "./MakeupSetupSteps"
import { ProductQrCodeDialog } from "../Glasses/ProductQrCodeDialog"
import { buildProductDemoUrl } from "./glassesDemoUrl"
import { buildUnifiedGlassesList, type UnifiedGlassesItem } from "./glassesRowModel"

const PAGE_SIZE_OPTIONS = [30, 50, 100]
const PAGE_SIZE_STORAGE_KEY = "productsPageSize"
const productTableHeadClassName = "text-sm font-medium text-foreground"

export const ProductsPage = ({ category }: { category: ProductCategories }) => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector(selectUserInfo)
  const { t } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()

  const isGlasses = category === ProductCategories.GLASSES

  const [showAddProductsModal, setShowAddProductsModal] = useState(false)
  const [uploadBulkMode, setUploadBulkMode] = useState(false)
  const [showInlineBulkUpload, setShowInlineBulkUpload] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [draftToDelete, setDraftToDelete] = useState<GlassesDraftListItem | null>(null)
  const [isProductQrDialogOpen, setIsProductQrDialogOpen] = useState(false)
  const [isGeneratingProductQrCode, setIsGeneratingProductQrCode] = useState(false)
  const [productQrCodeError, setProductQrCodeError] = useState("")
  const [productQrCodeName, setProductQrCodeName] = useState<string | undefined>(undefined)
  const [productQrCodeImage, setProductQrCodeImage] = useState<string | undefined>(undefined)
  const [productQrCodeResult, setProductQrCodeResult] = useState<IntegrationQrCodeResponse | null>(
    null
  )
  const [shareNavList, setShareNavList] = useState<
    Array<{ id: string; name: string; cover: string }>
  >([])
  const [shareNavIndex, setShareNavIndex] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const productListViewedKeyRef = useRef("")
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
    return stored ? parseInt(stored) : 30
  })

  const debouncedSearchText = useDebounce<string>(searchText, 300)
  const currentPage = parseInt(parseQuery(location.search).page) || 1
  const productsQueryParams: ProductsQueryParams = {
    page: currentPage,
    searchText: debouncedSearchText,
    category,
    merchantId: userInfo.merchantId ?? "",
    limit: pageSize,
  }
  const { data: productsResponse, isLoading } = useProductsQuery(productsQueryParams)
  const { data: glassesDrafts = [], isLoading: isDraftsLoading } = useGlassesDraftsQuery({
    enabled: isGlasses,
    merchantId: userInfo.merchantId ?? "",
    partial: debouncedSearchText,
  })

  const { pageCount, items: products } = productsResponse || { pageCount: 0, items: [] }
  const drafts = isGlasses ? glassesDrafts : []
  const isPageLoading = isLoading || (isGlasses && isDraftsLoading)
  const hasSavedOrDrafts = products.length > 0 || drafts.length > 0

  const unifiedGlasses = useMemo(
    () => (isGlasses ? buildUnifiedGlassesList(products, drafts) : []),
    [isGlasses, products, drafts]
  )

  const hasActiveGlassesWorkflow =
    isGlasses &&
    (drafts.some(
      (draft) =>
        draft.digitization.status === "queued" || draft.digitization.status === "processing"
    ) ||
      products.some(
        (product) =>
          product.digitization?.status === "queued" || product.digitization?.status === "processing"
      ))

  const hasActiveSearch = debouncedSearchText.length > 0
  const isInitialEmptyMakeup =
    !isGlasses && products.length === 0 && drafts.length === 0 && !hasActiveSearch && !isPageLoading
  const showInlineInitialMakeupActions =
    isInitialEmptyMakeup && userInfo.integrationType === IntegrationType.COMMON

  useEffect(() => {
    if (isPageLoading) return

    const viewKey = `${category}:${currentPage}:${debouncedSearchText}`
    if (productListViewedKeyRef.current === viewKey) return
    productListViewedKeyRef.current = viewKey

    capturePlatformEvent("product_list_viewed", {
      category,
      draft_count: drafts.length,
      has_search: hasActiveSearch,
      page: currentPage,
      page_size: pageSize,
      product_count: products.length,
    })
  }, [
    category,
    currentPage,
    debouncedSearchText,
    drafts.length,
    hasActiveSearch,
    isPageLoading,
    pageSize,
    products.length,
  ])

  const handleAddProduct = () => {
    if (userInfo.integrationType === IntegrationType.COMMON) {
      navigate(isGlasses ? "/glasses/add" : "/products/add")
      return
    }

    setUploadBulkMode(false)
    setShowAddProductsModal(true)
  }

  const handleOpenUpload = () => {
    setUploadBulkMode(true)
    setShowAddProductsModal(true)
  }

  useEffect(() => {
    if (!showInlineInitialMakeupActions) setShowInlineBulkUpload(false)
  }, [showInlineInitialMakeupActions])

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value)
    setPageSize(newSize)
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, value)
    const searchParams = new URLSearchParams(location.search)
    searchParams.set("page", "1")
    navigate(`${location.pathname}?${searchParams.toString()}`)
  }

  const { mutate: toggleVisibilityHandler } =
    useToggleProductVisibilityMutation(productsQueryParams)

  const { mutateAsync: deleteProductAsync } = useDeleteProductMutation(productsQueryParams)

  const openProductDemo = (product: Product, errorKey: string) => {
    const demoUrl = buildProductDemoUrl(userInfo.merchantId ?? "", product)

    if (!demoUrl) {
      showAlert({ error: true, text: t(errorKey) })
      return
    }

    window.open(demoUrl, "_blank", "noopener,noreferrer")
  }

  const handleMakeupViewDemo = (product: Product) =>
    openProductDemo(product, "makeup.action.viewDemoError")

  const handleGlassesViewDemo = (product: Product) =>
    openProductDemo(product, "glasses.action.viewDemoError")

  const runQrGeneration = async (params: {
    productId?: string
    productName?: string
    productImage?: string
  }) => {
    const merchantId = userInfo.merchantId?.trim()
    if (!merchantId) {
      showAlert({ error: true, text: t("integrationInfo.qrCode.generateError") })
      return
    }

    setIsProductQrDialogOpen(true)
    setIsGeneratingProductQrCode(true)
    setProductQrCodeError("")
    setProductQrCodeResult(null)
    setProductQrCodeName(params.productName)
    setProductQrCodeImage(params.productImage)

    try {
      setProductQrCodeResult(
        await adminApi.generateIntegrationQrCode({
          merchantId,
          ...(params.productId ? { productId: params.productId } : {}),
        })
      )
    } catch {
      setProductQrCodeError(getGenericUserErrorMessage(t))
    } finally {
      setIsGeneratingProductQrCode(false)
    }
  }

  const handleGenerateProductQrCode = (product: Product) => {
    const list = (
      isGlasses
        ? unifiedGlasses.flatMap((item) =>
            item.kind === "product"
              ? [{ id: item.product.id, name: item.product.name, cover: item.product.cover }]
              : []
          )
        : products.map((p) => ({ id: p.id, name: p.name, cover: p.cover }))
    ) as Array<{ id: string; name: string; cover: string }>
    const index = list.findIndex((p) => p.id === product.id)
    setShareNavList(list)
    setShareNavIndex(index < 0 ? 0 : index)
    void runQrGeneration({
      productId: product.id,
      productName: product.name,
      productImage: product.cover,
    })
  }

  const handleShareCatalog = () => {
    setShareNavList([])
    setShareNavIndex(0)
    void runQrGeneration({})
  }

  const handleShareNavigate = (delta: -1 | 1) => {
    const nextIndex = shareNavIndex + delta
    const target = shareNavList[nextIndex]
    if (!target) return
    setShareNavIndex(nextIndex)
    void runQrGeneration({
      productId: target.id,
      productName: target.name,
      productImage: target.cover,
    })
  }

  const handleEditProduct = (productId: string) => {
    navigate(`${location.pathname}/edit?id=${productId}`)
  }

  const handleOpenDraft = (draftId: string) => navigate(`/glasses/add?draftId=${draftId}`)

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const currentItemIds = useMemo(
    () => (isGlasses ? unifiedGlasses.map((item) => item.id) : products.map((p) => p.id)),
    [isGlasses, unifiedGlasses, products]
  )

  const handleToggleAll = (checked: boolean) => {
    setSelectedIds(() => {
      if (!checked) return new Set()
      return new Set(currentItemIds)
    })
  }

  const clearSelection = () => setSelectedIds(new Set())

  useEffect(() => {
    setSelectedIds(new Set())
  }, [category])

  useEffect(() => {
    if (selectedIds.size === 0) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") clearSelection()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedIds.size])

  const selectedGlassesItems = useMemo(
    () => unifiedGlasses.filter((item) => selectedIds.has(item.id)),
    [unifiedGlasses, selectedIds]
  )

  const selectableGlassesProducts = useMemo(
    () =>
      selectedGlassesItems.filter(
        (item): item is Extract<UnifiedGlassesItem, { kind: "product" }> =>
          item.kind === "product" && item.status === "ready" && !item.product.visible
      ),
    [selectedGlassesItems]
  )

  const selectedMakeupProducts = useMemo(
    () => products.filter((product) => selectedIds.has(product.id)),
    [products, selectedIds]
  )

  const selectableMakeupProducts = useMemo(
    () => selectedMakeupProducts.filter((product) => !product.visible),
    [selectedMakeupProducts]
  )

  const handleBulkTurnOn = async () => {
    const pool: Product[] = isGlasses
      ? selectableGlassesProducts.map((item) => item.product)
      : selectableMakeupProducts
    if (pool.length === 0) return
    setBulkBusy(true)
    try {
      await Promise.all(
        pool.map(
          (product) =>
            new Promise<void>((resolve) => {
              toggleVisibilityHandler(product, {
                onSettled: () => resolve(),
              })
            })
        )
      )
      showAlert({
        error: false,
        text: t(isGlasses ? "glasses.selection.turnedOn" : "makeup.selection.turnedOn", {
          count: pool.length,
        }),
      })
      clearSelection()
    } finally {
      setBulkBusy(false)
    }
  }

  const performBulkDelete = async () => {
    const glassesItems = isGlasses ? [...selectedGlassesItems] : []
    const makeupItems = !isGlasses ? [...selectedMakeupProducts] : []
    const totalCount = isGlasses ? glassesItems.length : makeupItems.length
    if (totalCount === 0) {
      setBulkDeleteOpen(false)
      return
    }

    setBulkDeleteOpen(false)
    clearSelection()
    setBulkBusy(true)
    let deletedCount = 0
    let failedCount = 0
    try {
      if (isGlasses)
        for (const item of glassesItems)
          try {
            if (item.kind === "product") {
              await deleteProductAsync(item.product as unknown as ProductV2)
            } else {
              await tintVTOService.deleteGlassesDraft(item.draft.draftId)
              queryClient.setQueriesData<GlassesDraftListItem[]>(
                { queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY },
                (old) => old?.filter((d) => d.draftId !== item.draft.draftId)
              )
            }
            deletedCount++
          } catch {
            failedCount++
          }
      else
        for (const product of makeupItems)
          try {
            await deleteProductAsync(product as unknown as ProductV2)
            deletedCount++
          } catch {
            failedCount++
          }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() }),
        queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY }),
      ])

      if (deletedCount > 0)
        showAlert({
          error: false,
          text: t(
            isGlasses ? "glasses.selection.deleteSuccess" : "makeup.selection.deleteSuccess",
            { count: deletedCount }
          ),
        })
      if (failedCount > 0)
        showAlert({
          error: true,
          text: t("productsPage.deleteError"),
        })
    } finally {
      setBulkBusy(false)
    }
  }

  const handleCancelDraft = (draftId: string) => {
    const draft = drafts.find((d) => d.draftId === draftId)
    if (draft) setDraftToDelete(draft)
  }

  const hasProductsOrSearch =
    products.length > 0 || drafts.length > 0 || debouncedSearchText.length > 0 || isPageLoading
  const showEmptyState =
    products.length === 0 &&
    drafts.length === 0 &&
    debouncedSearchText.length === 0 &&
    !isPageLoading
  const currentGlassesItemCount = isGlasses ? unifiedGlasses.length : products.length

  useEffect(() => {
    if (currentGlassesItemCount === 0 && currentPage > 1 && !isPageLoading)
      navigate(`?page=${currentPage - 1}`)
  }, [currentGlassesItemCount, currentPage, isPageLoading, navigate])

  useEffect(() => {
    if (!hasActiveGlassesWorkflow) return

    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() })
      queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
    }, 5000)

    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() })
      queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
    }

    window.addEventListener("focus", handleFocus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener("focus", handleFocus)
    }
  }, [hasActiveGlassesWorkflow, queryClient])

  const selectedCount = selectedIds.size
  const allSelected = currentItemIds.length > 0 && selectedCount === currentItemIds.length
  const indeterminate = selectedCount > 0 && !allSelected
  const showSelectionBar = selectedCount > 0
  const canTurnOnBulk = isGlasses
    ? selectableGlassesProducts.length > 0
    : selectableMakeupProducts.length > 0
  const bulkConfirmText = t(
    isGlasses ? "glasses.selection.confirmDelete" : "makeup.selection.confirmDelete",
    { count: selectedCount }
  )

  const addProductLabel =
    userInfo.integrationType === IntegrationType.TIENDANUBE
      ? t("productsPage.selectSKU")
      : t("productsPage.addProduct")

  const showUploadCta = !isGlasses && userInfo.integrationType === IntegrationType.COMMON
  const productPageTestId = isGlasses ? "products-page-glasses" : "products-page-makeup"
  const productTableTestId = isGlasses ? "products-table-glasses" : "products-table-makeup"

  return (
    <div
      className="mx-auto w-full max-w-[1440px] min-w-0 p-4 md:p-6"
      data-testid={productPageTestId}
    >
      <LeaveOpinionFeatureModal type={ActivatedOpinionPopup.OPINION_AFTER_INITIAL_SETUP} />
      <ConfirmDialog
        isOpen={!!draftToDelete}
        onClose={() => setDraftToDelete(null)}
        onConfirm={() => {
          if (!draftToDelete) return
          const draft = draftToDelete
          setDraftToDelete(null)
          queryClient.setQueriesData<GlassesDraftListItem[]>(
            { queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY },
            (old) => old?.filter((d) => d.draftId !== draft.draftId)
          )
          tintVTOService
            .deleteGlassesDraft(draft.draftId)
            .then(() =>
              showAlert({
                error: false,
                text: t("glasses.draft.cancelSuccess"),
              })
            )
            .catch(() => {
              queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
              showAlert({
                error: true,
                text: getGenericUserErrorMessage(t),
              })
            })
        }}
        text={t("glasses.draft.cancelConfirm")}
      />
      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => void performBulkDelete()}
        text={bulkConfirmText}
      />
      <ProductQrCodeDialog
        error={productQrCodeError}
        isGenerating={isGeneratingProductQrCode}
        onOpenChange={setIsProductQrDialogOpen}
        open={isProductQrDialogOpen}
        productName={productQrCodeName}
        productImage={productQrCodeImage}
        result={productQrCodeResult}
        currentIndex={shareNavList.length > 0 ? shareNavIndex : undefined}
        totalCount={shareNavList.length > 0 ? shareNavList.length : undefined}
        onPrevious={shareNavList.length > 0 ? () => handleShareNavigate(-1) : undefined}
        onNext={shareNavList.length > 0 ? () => handleShareNavigate(1) : undefined}
      />
      {showAddProductsModal && (
        <AddProductsModal
          category={category}
          onAlert={showAlert}
          isOpen={showAddProductsModal}
          onClose={() => {
            setShowAddProductsModal(false)
            setUploadBulkMode(false)
          }}
          {...(uploadBulkMode && userInfo.integrationType === IntegrationType.COMMON
            ? { initialStep: 2, initialType: "bulk" }
            : {})}
        />
      )}
      <ProductsPageHeader
        testIdPrefix={isGlasses ? "glasses-products" : "makeup-products"}
        title={t("productsPage.setupTitle")}
        steps={showEmptyState || isGlasses ? <GlassesSetupSteps /> : <MakeupSetupSteps />}
        showSearch={hasSavedOrDrafts || hasActiveSearch}
        showAddCtas={hasSavedOrDrafts}
        showUpload={showUploadCta}
        onUpload={handleOpenUpload}
        onAdd={handleAddProduct}
        addLabel={addProductLabel}
        uploadLabel={t("productsPage.uploadProductList")}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t("productsPage.searchPlaceholder")}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onShareCatalog={hasSavedOrDrafts ? handleShareCatalog : undefined}
        shareCatalogLabel={t("integrationInfo.qrCode.shareCatalog")}
        shareCatalogPlacement="top"
        addVariant="dark"
      />

      {hasProductsOrSearch && !isPageLoading ? (
        isGlasses ? (
          <>
            {unifiedGlasses.length > 0 ? (
              <Card className="min-w-0 overflow-hidden" data-testid={productTableTestId}>
                <Table className="w-full min-w-[940px] table-fixed">
                  <colgroup>
                    <col className="w-[44px]" />
                    <col />
                    <col className="w-[112px]" />
                    <col className="w-[132px]" />
                    <col className="w-[292px]" />
                  </colgroup>
                  <TableHeader>
                    {showSelectionBar ? (
                      <GlassesSelectionToolbar
                        selectedCount={selectedCount}
                        allSelected={allSelected}
                        indeterminate={indeterminate}
                        onToggleAll={handleToggleAll}
                        onTurnOn={() => void handleBulkTurnOn()}
                        onDelete={() => setBulkDeleteOpen(true)}
                        onClear={clearSelection}
                        canTurnOn={canTurnOnBulk}
                        turningOn={bulkBusy}
                        deleting={bulkBusy}
                      />
                    ) : (
                      <TableRow className="group bg-muted/50 hover:bg-muted/50">
                        <TableHead className="pl-3">
                          <HeaderSelectAllCheckbox
                            allSelected={allSelected}
                            indeterminate={indeterminate}
                            onToggle={handleToggleAll}
                          />
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.product")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.status")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("glasses.table.tryOnButton")}
                        </TableHead>
                        <TableHead className={`${productTableHeadClassName} text-right`}>
                          {t("glasses.table.actions")}
                        </TableHead>
                      </TableRow>
                    )}
                  </TableHeader>
                  <TableBody>
                    {unifiedGlasses.map((item) => (
                      <GlassesRow
                        key={item.id}
                        item={item}
                        selected={selectedIds.has(item.id)}
                        onToggleSelect={toggleRowSelection}
                        onViewDemo={handleGlassesViewDemo}
                        onEditProduct={handleEditProduct}
                        onGenerateQrCode={(product) => void handleGenerateProductQrCode(product)}
                        onOpenDraft={handleOpenDraft}
                        onCancelDraft={handleCancelDraft}
                        toggleVisibility={(product: Product) => toggleVisibilityHandler(product)}
                      />
                    ))}
                  </TableBody>
                </Table>
                {pageCount > 1 && (
                  <div className="border-t px-4 py-3">
                    <Paginator page={currentPage} pageCount={pageCount} />
                  </div>
                )}
              </Card>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
                <p className="text-sm text-muted-foreground">
                  {hasActiveSearch
                    ? t("productsPage.noSearchResults", { query: debouncedSearchText })
                    : t("productsPage.emptyText")}
                </p>
                {hasActiveSearch && (
                  <Button variant="outline" size="sm" onClick={() => setSearchText("")}>
                    {t("productsPage.clearSearch")}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {products.length > 0 ? (
              <Card className="min-w-0 overflow-hidden" data-testid={productTableTestId}>
                <Table className="w-full min-w-[940px] table-fixed">
                  <colgroup>
                    <col className="w-[44px]" />
                    <col />
                    <col className="w-[128px]" />
                    <col className="w-[112px]" />
                    <col className="w-[132px]" />
                    <col className="w-[292px]" />
                  </colgroup>
                  <TableHeader>
                    {showSelectionBar ? (
                      <MakeupSelectionToolbar
                        selectedCount={selectedCount}
                        allSelected={allSelected}
                        indeterminate={indeterminate}
                        onToggleAll={handleToggleAll}
                        onTurnOn={() => void handleBulkTurnOn()}
                        onDelete={() => setBulkDeleteOpen(true)}
                        onClear={clearSelection}
                        canTurnOn={canTurnOnBulk}
                        turningOn={bulkBusy}
                        deleting={bulkBusy}
                      />
                    ) : (
                      <TableRow className="group bg-muted/50 hover:bg-muted/50">
                        <TableHead className="pl-3">
                          <HeaderSelectAllCheckbox
                            allSelected={allSelected}
                            indeterminate={indeterminate}
                            onToggle={handleToggleAll}
                          />
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.product")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.type")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.status")}
                        </TableHead>
                        <TableHead className={productTableHeadClassName}>
                          {t("makeup.table.tryOnButton")}
                        </TableHead>
                        <TableHead className={`${productTableHeadClassName} text-right`}>
                          {t("makeup.table.actions")}
                        </TableHead>
                      </TableRow>
                    )}
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <MakeupRow
                        key={product.id}
                        product={product}
                        selected={selectedIds.has(product.id)}
                        onToggleSelect={toggleRowSelection}
                        onViewDemo={handleMakeupViewDemo}
                        onGenerateQrCode={(product) => void handleGenerateProductQrCode(product)}
                        onEdit={handleEditProduct}
                        toggleVisibility={(p: Product) => toggleVisibilityHandler(p)}
                      />
                    ))}
                  </TableBody>
                </Table>
                {pageCount > 1 && (
                  <div className="border-t px-4 py-3">
                    <Paginator page={currentPage} pageCount={pageCount} />
                  </div>
                )}
              </Card>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12">
                <p className="text-sm text-muted-foreground">
                  {hasActiveSearch
                    ? t("productsPage.noSearchResults", { query: debouncedSearchText })
                    : t("productsPage.emptyText")}
                </p>
                {hasActiveSearch && (
                  <Button variant="outline" size="sm" onClick={() => setSearchText("")}>
                    {t("productsPage.clearSearch")}
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : null}

      {showEmptyState && (
        <div className="flex items-center justify-center py-24" data-testid="products-empty-state">
          {showInlineInitialMakeupActions ? (
            showInlineBulkUpload ? (
              <div className="w-full max-w-2xl" data-testid="products-inline-bulk-upload">
                <UploadBulkProducts
                  onAlert={showAlert}
                  backHandler={() => setShowInlineBulkUpload(false)}
                  onClose={() => setShowInlineBulkUpload(false)}
                />
              </div>
            ) : (
              <div
                className="mx-auto flex w-full max-w-md flex-col gap-3"
                data-testid="products-empty-actions"
              >
                <AddProductCard
                  variant="primary"
                  badge={t("addProductStepOne.recommendedBadge")}
                  icon={<PenLine className="h-5 w-5" />}
                  title={t("addProductStepOne.manualTitle")}
                  description={t("addProductStepOne.manualDescription")}
                  onClick={() => navigate("/products/add")}
                />
                <AddProductCard
                  icon={<FileSpreadsheet className="h-5 w-5" />}
                  title={t("addProductStepOne.bulkTitle")}
                  description={t("addProductStepOne.bulkDescription")}
                  onClick={() => setShowInlineBulkUpload(true)}
                />
              </div>
            )
          ) : (
            <EmptyList
              text={t("productsPage.emptyText")}
              addText={addProductLabel}
              onClick={handleAddProduct}
              showAdd={debouncedSearchText === ""}
            />
          )}
        </div>
      )}
      {isPageLoading && (
        <Card className="min-w-0 overflow-hidden" data-testid="products-loading-state">
          <div className="space-y-5 p-6">
            <div className="flex items-center justify-between gap-3">
              <Skeleton width="w-40" height="h-6" />
              <Skeleton width="w-28" height="h-9" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className={
                    isGlasses
                      ? "grid grid-cols-[44px_1fr_112px_132px_292px] items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                      : "grid grid-cols-[44px_1fr_128px_112px_132px_292px] items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0"
                  }
                >
                  <Skeleton width="w-4" height="h-4" />
                  <Skeleton width="w-48" height="h-10" />
                  {!isGlasses && <Skeleton width="w-16" height="h-4" />}
                  <Skeleton width="w-20" height="h-6" />
                  <Skeleton width="w-24" height="h-4" />
                  <Skeleton width="w-48" height="h-8" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

interface ProductsPageHeaderProps {
  testIdPrefix: string
  title: string
  steps: ReactNode
  showSearch: boolean
  showAddCtas: boolean
  showUpload: boolean
  onUpload: () => void
  onAdd: () => void
  addLabel: string
  uploadLabel: string
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder: string
  pageSize: number
  onPageSizeChange: (value: string) => void
  onShareCatalog?: () => void
  shareCatalogLabel: string
  shareCatalogPlacement?: "top" | "inline"
  addVariant?: "accent" | "dark"
}

const ADD_CTA_WIDTH = "w-[8.625rem]" // 138px
const WIDE_CTA_WIDTH = "w-[11.25rem]" // 180px

const ProductsPageHeader = ({
  testIdPrefix,
  title,
  steps,
  showSearch,
  showAddCtas,
  showUpload,
  onUpload,
  onAdd,
  addLabel,
  uploadLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  pageSize,
  onPageSizeChange,
  onShareCatalog,
  shareCatalogLabel,
  shareCatalogPlacement = "top",
  addVariant = "accent",
}: ProductsPageHeaderProps) => {
  const showShareCatalogInline = shareCatalogPlacement === "inline" && Boolean(onShareCatalog)
  const showShareCatalogTop = shareCatalogPlacement === "top" && Boolean(onShareCatalog)
  const topActions = showShareCatalogTop ? (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        size="sm"
        onClick={onShareCatalog}
        data-testid={`${testIdPrefix}-share-catalog`}
        className={`${WIDE_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`}
      >
        <Share2 className="mr-1.5 h-3.5 w-3.5" />
        {shareCatalogLabel}
      </Button>
    </div>
  ) : null

  const addButtonClassName =
    addVariant === "dark"
      ? `${ADD_CTA_WIDTH} bg-foreground text-background shadow-sm hover:bg-foreground/90 focus-visible:ring-foreground`
      : `${ADD_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`

  return (
    <div className="mb-6" data-testid={`${testIdPrefix}-header`}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="products-page-title">
            {title}
          </h1>
          {steps}
        </div>
        {topActions}
      </div>
      {(showSearch || showAddCtas) && (
        <div className="mt-6 flex min-w-0 flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {showSearch && (
              <>
                <div className="relative min-w-[240px] max-w-sm flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/55" />
                  <Input
                    data-testid={`${testIdPrefix}-search`}
                    name="searchbar"
                    value={searchValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-9 pl-9 text-sm placeholder:text-foreground/55"
                  />
                  {searchValue && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                      data-testid={`${testIdPrefix}-clear-search`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <PageSizeSelector
                  value={pageSize}
                  onChange={onPageSizeChange}
                  testId={`${testIdPrefix}-page-size`}
                />
              </>
            )}
          </div>
          {showAddCtas && (
            <div className="flex shrink-0 items-center gap-2">
              {showUpload && (
                <Button
                  size="sm"
                  variant="outline"
                  className={WIDE_CTA_WIDTH}
                  onClick={onUpload}
                  data-testid={`${testIdPrefix}-upload`}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                  {uploadLabel}
                </Button>
              )}
              {showShareCatalogInline ? (
                <Button
                  size="sm"
                  onClick={onShareCatalog}
                  data-testid={`${testIdPrefix}-share-catalog`}
                  className={`${WIDE_CTA_WIDTH} bg-vto-accent text-vto-accent-foreground shadow-sm hover:bg-vto-accent/90 focus-visible:ring-vto-accent`}
                >
                  <Share2 className="mr-1.5 h-3.5 w-3.5" />
                  {shareCatalogLabel}
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={onAdd}
                className={addButtonClassName}
                data-testid={`${testIdPrefix}-add`}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {addLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const PageSizeSelector = ({
  value,
  onChange,
  testId,
}: {
  value: number
  onChange: (value: string) => void
  testId: string
}) => (
  <div className="flex items-center gap-2" data-testid={`${testId}-container`}>
    <span className="text-xs text-foreground/70">Show</span>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[75px]" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent data-testid={`${testId}-options`}>
        {PAGE_SIZE_OPTIONS.map((size) => (
          <SelectItem key={size} value={String(size)} data-testid={`${testId}-option-${size}`}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)

const HeaderSelectAllCheckbox = ({
  allSelected,
  indeterminate,
  onToggle,
}: {
  allSelected: boolean
  indeterminate: boolean
  onToggle: (checked: boolean) => void
}) => (
  <Checkbox
    aria-label="Select all"
    data-testid="products-select-all"
    checked={indeterminate ? "indeterminate" : allSelected}
    className={
      allSelected || indeterminate
        ? ""
        : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100"
    }
    onCheckedChange={(value) => onToggle(Boolean(value))}
  />
)
